import {
  getWhatsAppSettings as fetchWhatsAppSettings,
  saveWhatsAppSettings as putWhatsAppSettings,
  getWhatsAppErrorMessage,
  isWhatsAppForbiddenError,
  isWhatsAppApiSuccess,
} from "./whatsappApi";

/** @typedef {import('../types/whatsappSetup').WhatsAppSettingsPublic} WhatsAppSettingsPublic */
/** @typedef {import('../types/whatsappSetup').WhatsAppSettingsSavePayload} WhatsAppSettingsSavePayload */

export function normalizeWhatsAppSettings(res) {
  const data = res?.data ?? res ?? {};
  return {
    apiVersion: data.apiVersion ?? data.api_version ?? "v21.0",
    phoneNumberId: data.phoneNumberId ?? data.phone_number_id ?? null,
    wabaId: data.wabaId ?? data.waba_id ?? null,
    appId: data.appId ?? data.app_id ?? null,
    configured: Boolean(data.configured),
    hasAccessToken: Boolean(data.hasAccessToken ?? data.has_access_token),
    hasAppSecret: Boolean(data.hasAppSecret ?? data.has_app_secret),
    hasWebhookVerifyToken: Boolean(
      data.hasWebhookVerifyToken ?? data.has_webhook_verify_token,
    ),
  };
}

export async function getSettings() {
  const res = await fetchWhatsAppSettings();
  if (res?.success === false) {
    const err = new Error(res.message || "Failed to load WhatsApp settings");
    err.response = { status: res.statusCode || 400, data: res };
    throw err;
  }
  return normalizeWhatsAppSettings(res);
}

export async function saveSettings(payload) {
  const res = await putWhatsAppSettings(payload);
  if (!isWhatsAppApiSuccess(res) && res?.success === false) {
    const err = new Error(res.message || "Failed to save WhatsApp settings");
    err.response = { status: res.statusCode || 400, data: res };
    throw err;
  }
  return normalizeWhatsAppSettings(res);
}

export function getSettingsErrorMessage(err, fallback = "Request failed") {
  if (isWhatsAppForbiddenError(err)) {
    return "You don't have permission to configure WhatsApp";
  }
  return getWhatsAppErrorMessage(err, fallback);
}

export function buildSettingsSavePayload(form, existing) {
  /** @type {WhatsAppSettingsSavePayload} */
  const body = {};
  const isFirstSetup = !existing?.configured;

  const accessToken = String(form.access_token || "").trim();
  const phoneNumberId = String(form.phone_number_id || "").trim();
  const wabaId = String(form.waba_id || "").trim();
  const appId = String(form.app_id || "").trim();
  const appSecret = String(form.app_secret || "").trim();
  const webhookVerifyToken = String(form.webhook_verify_token || "").trim();

  if (isFirstSetup) {
    body.access_token = accessToken;
    body.phone_number_id = phoneNumberId;
    body.waba_id = wabaId;
  } else {
    if (phoneNumberId) body.phone_number_id = phoneNumberId;
    if (wabaId) body.waba_id = wabaId;
    if (accessToken) body.access_token = accessToken;
  }

  if (appId) body.app_id = appId;
  if (appSecret) body.app_secret = appSecret;
  if (webhookVerifyToken) body.webhook_verify_token = webhookVerifyToken;

  return body;
}

export function validateSettingsForm(form, existing) {
  const errors = {};
  const isFirstSetup = !existing?.configured;

  if (isFirstSetup) {
    if (!String(form.access_token || "").trim()) {
      errors.access_token = "Meta Access Token is required";
    }
    if (!String(form.phone_number_id || "").trim()) {
      errors.phone_number_id = "Phone Number ID is required";
    }
    if (!String(form.waba_id || "").trim()) {
      errors.waba_id = "WABA ID is required";
    }
  }

  return errors;
}
